import { kitchenApi } from './kitchenApi';
import type {
    BookingActivity,
    BookingSlot,
    BookingSlotRequest
} from '../api-types/models/Booking';

export const bookingApi = kitchenApi.injectEndpoints({
    endpoints: (builder) => ({
        getBookingRooms: builder.query<string[], void>({
            query: () => ({
                apiName: 'brunch',
                path: '/activities/rooms',
                method: 'GET'
            }),
            providesTags: ['Booking']
        }),

        getBookingActivities: builder.query<BookingActivity[], string>({
            query: (room) => ({
                apiName: 'brunch',
                path: `/activities/${room.toUpperCase()}/list`,
                method: 'GET'
            }),
            providesTags: (_r, _e, room) => [
                { type: 'BookingActivities', id: room }
            ]
        }),

        getSlotsForWeek: builder.query<BookingSlot[], string>({
            query: (date) => ({
                apiName: 'brunch',
                path: `/activities/calendar/${date}/week`,
                method: 'GET'
            }),
            providesTags: ['BookingSlots']
        }),

        getSlotsByDate: builder.query<BookingSlot[], string>({
            query: (date) => ({
                apiName: 'brunch',
                path: `/activities/calendar/${date}`,
                method: 'GET'
            }),
            transformResponse: (response: BookingSlot[]) =>
                [...response].sort((a, b) =>
                    a.startDateTime < b.startDateTime ? -1 : 1
                ),
            providesTags: (_r, _e, date) => [{ type: 'BookingSlots', id: date }]
        }),

        getBookingsForActiveMonth: builder.query<BookingSlot[], string>({
            query: (date) => ({
                apiName: 'brunch',
                path: `/activities/booking/${date}/month`,
                method: 'GET'
            }),
            providesTags: ['Booking']
        }),

        addBookingActivity: builder.mutation<BookingActivity, BookingActivity>({
            query: (body) => ({
                apiName: 'brunch',
                path: '/activities',
                method: 'POST',
                body
            }),
            invalidatesTags: ['BookingActivities']
        }),

        updateBookingActivity: builder.mutation<
            BookingActivity,
            BookingActivity
        >({
            query: (body) => ({
                apiName: 'brunch',
                path: '/activities',
                method: 'PUT',
                body
            }),
            invalidatesTags: ['BookingActivities']
        }),

        deleteBookingActivity: builder.mutation<void, BookingActivity>({
            query: (activity) => ({
                apiName: 'brunch',
                path: `/activities/room/${activity.room}/id/${activity.id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['BookingActivities']
        }),

        addBookingSlot: builder.mutation<BookingSlot, BookingSlotRequest>({
            query: (body) => ({
                apiName: 'brunch',
                path: '/activities/slot',
                method: 'POST',
                body
            }),
            invalidatesTags: ['BookingSlots']
        }),

        updateBookingSlot: builder.mutation<
            BookingSlot,
            { slot: BookingSlotRequest; id: string }
        >({
            query: ({ slot, id }) => ({
                apiName: 'brunch',
                path: `/activities/slot/${id}`,
                method: 'PUT',
                body: slot
            }),
            invalidatesTags: ['BookingSlots']
        }),

        deleteSlot: builder.mutation<void, string>({
            query: (slotId) => ({
                apiName: 'brunch',
                path: `/activities/slot/${slotId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['BookingSlots']
        }),

        bookSlot: builder.mutation<BookingSlot, string>({
            query: (slotId) => ({
                apiName: 'brunch',
                path: '/activities/booking',
                method: 'POST',
                body: { slotId }
            }),
            invalidatesTags: ['BookingSlots', 'Booking']
        }),

        removeBooking: builder.mutation<BookingSlot, string>({
            query: (slotId) => ({
                apiName: 'brunch',
                path: `/activities/booking/${slotId}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['BookingSlots', 'Booking']
        }),

        getSlotInfo: builder.mutation<
            BookingSlot,
            { startDateTime: number; room: string; durationInMinutes: number }
        >({
            query: (body) => ({
                apiName: 'brunch',
                path: '/booking/slot/custom/info',
                method: 'POST',
                body
            })
        }),

        updateBookingGuests: builder.mutation<
            BookingSlot,
            { slotId: string; guests: string[] }
        >({
            query: ({ slotId, guests }) => ({
                apiName: 'brunch',
                path: `/activities/booking/${slotId}`,
                method: 'PUT',
                body: guests
            }),
            invalidatesTags: ['BookingSlots']
        })
    }),
    overrideExisting: true
});

export const {
    useGetBookingRoomsQuery,
    useGetBookingActivitiesQuery,
    useGetSlotsForWeekQuery,
    useGetSlotsByDateQuery,
    useGetBookingsForActiveMonthQuery,
    useAddBookingActivityMutation,
    useUpdateBookingActivityMutation,
    useDeleteBookingActivityMutation,
    useAddBookingSlotMutation,
    useUpdateBookingSlotMutation,
    useDeleteSlotMutation,
    useBookSlotMutation,
    useRemoveBookingMutation,
    useGetSlotInfoMutation,
    useUpdateBookingGuestsMutation
} = bookingApi;
